import React from 'react';
import {useTranslation} from 'react-i18next';
import {PakistanPhoneInputField} from '../../login/components/pakistan-phone-input-field';
import {InvitationApplyViewModel} from './use-invitation-apply';
import {OtpIcon, PhoneIcon} from './InvitationApplyIcons';

const signUpGifts = require('@/assets/imgs/invitation/sign-up-gifts.webp');

export type InvitationApplyFormClassNames = {
  card: string;
  content: string;
  error: string;
  fields: string;
  loginButton: string;
  otpField: string;
  phoneField: string;
  phoneWrapper: string;
  footer: string;
};

type InvitationApplyFormCardProps = {
  classNames: InvitationApplyFormClassNames;
  viewModel: InvitationApplyViewModel;
};

function InvitationApplyFormBackground() {
  return (
    <div
      className="pointer-events-none absolute inset-0 border-x-0 border-b-[0.5rem] border-t-[3.75rem] border-solid [border-image-repeat:stretch] [border-image-slice:180_0_24_fill] [border-image-width:3.75rem_0_0.5rem]"
      style={{borderImageSource: `url(${signUpGifts})`}}
    />
  );
}

export function InvitationApplyFormCard({
  classNames,
  viewModel,
}: InvitationApplyFormCardProps) {
  const {t} = useTranslation();
  const {
    handleLogin,
    handleSendCode,
    hasOTP,
    OTPCode,
    OTPLoading,
    OTPTime,
    phoneValidation,
    setOTPCode,
    setUserPhone,
    userPhone,
  } = viewModel;

  return (
    <div className={classNames.card}>
      <InvitationApplyFormBackground />
      <div className={classNames.content}>
        <div className={classNames.fields}>
          <PakistanPhoneInputField
            value={userPhone}
            setValue={setUserPhone}
            validation={phoneValidation}
            errorId="invitation-phone-error"
            prefix={<PhoneIcon />}
            placeholder={t('invitation.apply.phoneNumber')}
            wrapperClassName={classNames.phoneWrapper}
            containerClassName={[
              classNames.phoneField,
              'flex items-center rounded-[0.625rem] border bg-[#24242B] px-2.5',
              phoneValidation.hasError ? 'border-[#FF4E37]' : 'border-white/10',
            ].join(' ')}
            inputClassName="ml-2.5 min-w-0 flex-1 border-0 bg-transparent p-0 text-[0.9375rem] font-medium leading-5 text-white outline-none placeholder:text-white/35"
            clearButtonClassName="relative ml-2 h-4 w-4 shrink-0 rounded-full border-0 bg-white/20 p-0 transition-opacity before:absolute before:left-1/2 before:top-1/2 before:h-2.5 before:w-px before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:rounded-full before:bg-white/80 after:absolute after:left-1/2 after:top-1/2 after:h-2.5 after:w-px after:-translate-x-1/2 after:-translate-y-1/2 after:-rotate-45 after:rounded-full after:bg-white/80"
            errorClassName={classNames.error}
          />

          <label
            className={`${classNames.otpField} flex items-center rounded-[0.625rem] border border-white/10 bg-[#24242B] px-2.5`}>
            <OtpIcon />
            <input
              value={OTPCode}
              maxLength={6}
              inputMode="numeric"
              placeholder={t('invitation.apply.otp')}
              onChange={event =>
                setOTPCode(event.currentTarget.value.replace(/\D/g, ''))
              }
              className="ml-2.5 min-w-0 flex-1 border-0 bg-transparent p-0 text-[0.9375rem] font-medium leading-5 text-white outline-none placeholder:text-white/35"
            />
            {hasOTP ? (
              <span className="ml-2 shrink-0 text-[0.875rem] font-semibold leading-5 text-[#00CC68]">
                {OTPTime}s
              </span>
            ) : (
              <button
                type="button"
                disabled={OTPLoading}
                onClick={handleSendCode}
                className="ml-2 flex h-7 w-[3.75rem] shrink-0 items-center justify-center rounded-full border-0 bg-[linear-gradient(180deg,#00FAA0_0%,#00F957_100%)] p-0 text-[0.75rem] font-medium leading-[0.9375rem] text-black disabled:bg-[linear-gradient(180deg,#4B4B52_0%,#303037_100%)] disabled:text-white/35">
                {t('invitation.apply.send')}
              </button>
            )}
          </label>
        </div>

        <div className="flex flex-col items-center">
          <button
            type="button"
            disabled={!OTPCode || OTPLoading}
            onClick={handleLogin}
            className={`${classNames.loginButton} mx-auto flex w-full max-w-[19.5625rem] items-center justify-center rounded-[1.375rem] border border-[#8AFFD4] bg-[linear-gradient(180deg,#00FAA0_0%,#00F957_100%)] text-[1rem] font-semibold leading-5 text-[#1A1921] disabled:border-white/10 disabled:bg-[linear-gradient(180deg,#00FAA0_0%,#00F957_100%)] disabled:opacity-45`}>
            {t('invitation.apply.logIn')}
          </button>

          <p
            className={`${classNames.footer} w-[15.6875rem] text-center text-[0.625rem] font-normal leading-4 text-[#666666]`}>
            {t('invitation.apply.autoRegisterLine1')}
            <br />
            {t('invitation.apply.autoRegisterLine2')}
          </p>
        </div>
      </div>
    </div>
  );
}
